import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";

export default function Surveys() {
  const [items, setItems] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", branch: "",
    questions: [{ id: "q1", text: "" }, { id: "q2", text: "" }, { id: "q3", text: "" }],
  });

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: s }, { data: r }] = await Promise.all([
          api.get("/surveys"), api.get("/survey-responses"),
        ]);
        if (!cancelled) { setItems(s); setResponses(r); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/surveys", {
        title: form.title, branch: form.branch || null,
        questions: form.questions.map((q) => q.text).filter((t) => t.trim()),
      });
      toast.success("Survey created");
      setOpen(false);
      setForm({ title: "", branch: "",
        questions: [{ id: "q1", text: "" }, { id: "q2", text: "" }, { id: "q3", text: "" }] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this survey?")) return;
    await api.delete(`/surveys/${id}`);
    load();
  };

  return (
    <div>
      <PageHeader
        eyebrow="Stakeholders"
        title="Customer Surveys"
        subtitle="Build feedback surveys and collect responses across branches."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="survey-new-button">
                <Plus size={16} className="mr-1.5" /> Create survey
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">New survey</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="survey-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1" data-testid="survey-title-input" />
                </div>
                <div>
                  <Label>Target branch (optional)</Label>
                  <Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    className="mt-1" placeholder="All branches" />
                </div>
                {form.questions.map((q, i) => (
                  <div key={q.id}>
                    <Label>Question {i + 1}</Label>
                    <Input value={q.text}
                      onChange={(e) => {
                        const next = [...form.questions];
                        next[i] = { ...next[i], text: e.target.value };
                        setForm({ ...form, questions: next });
                      }}
                      className="mt-1"
                      data-testid={`survey-q${i}-input`} />
                  </div>
                ))}
                <Button type="button" variant="ghost" size="sm"
                  onClick={() => setForm({ ...form, questions: [...form.questions, { id: `q${Date.now()}`, text: "" }] })}>
                  + Add question
                </Button>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="survey-submit-button">
                    {busy ? "Saving…" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No surveys yet" hint="Create your first customer survey." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="surveys-grid">
          {items.map((s) => {
            const respCount = responses.filter((r) => r.survey_id === s.id).length;
            return (
              <div key={s.id} className="bg-white border border-gray-200 rounded-md p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-gray-900">{s.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.branch || "All branches"} · {s.questions?.length || 0} questions</div>
                  </div>
                  <Badge tone={s.is_active === false ? "gray" : "emerald"}>
                    {s.is_active === false ? "Inactive" : "Active"}
                  </Badge>
                </div>
                <ul className="text-xs text-gray-700 mt-3 space-y-1">
                  {(s.questions || []).slice(0, 3).map((q, i) => (
                    <li key={`${s.id}-q-${i}`} className="flex gap-1.5"><ChevronRight size={11} className="text-emerald-700 mt-0.5 flex-shrink-0" /> {q}</li>
                  ))}
                </ul>
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <span className="text-xs font-semibold text-emerald-900">{respCount} response(s)</span>
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id)}
                    className="text-red-700 hover:bg-red-50"
                    data-testid={`survey-delete-${s.id}`}>
                    <Trash2 size={13} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
